import React, { useState, useMemo, useEffect } from "react";
import { ArrowRight, X } from "lucide-react";
import WatersVariantA from "./WatersVariantA";
import WatersVariantB from "./WatersVariantB";
import WatersVariantC from "./WatersVariantC";
import analyticsService from "../../lib/AnalyticsService";

const VARIANTS = ["A", "B", "C"];
const SESSION_KEY = "waters_welcome_variant";

const getVariant = () => {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored && VARIANTS.includes(stored)) return stored;
  const picked = VARIANTS[Math.floor(Math.random() * VARIANTS.length)];
  sessionStorage.setItem(SESSION_KEY, picked);
  return picked;
};

const WatersWelcome = ({ onComplete, trackButtonClick }) => {
  const [showOwnliModal, setShowOwnliModal] = useState(false);
  const variant = useMemo(() => getVariant(), []);

  useEffect(() => {
    analyticsService.trackPageView("waters_welcome", {
      waters_variant: variant,
      partner_name: "Waters",
    });
  }, [variant]);

  const VariantComponent =
    variant === "A" ? WatersVariantA : variant === "B" ? WatersVariantB : WatersVariantC;

  return (
    <div className="space-y-5 p-4 pt-5 pb-6">
      {/* Variant Body */}
      <VariantComponent />

      {/* Learn More — shared */}
      <div className="text-center">
        <p className="text-sm text-darkGrey">
          Why Waters partnered with Ownli?{" "}
          <button
            onClick={() => {
              trackButtonClick("why_waters_partnered_ownli", {
                step_name: "welcome",
                waters_variant: variant,
              });
              analyticsService.trackPageView("waters_why_partnered_modal", {
                waters_variant: variant,
              });
              setShowOwnliModal(true);
            }}
            className="text-indigo-600 hover:text-indigo-700 underline font-semibold"
          >
            Learn More
          </button>
        </p>
      </div>

      {/* Ownli Modal */}
      {showOwnliModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowOwnliModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto space-y-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowOwnliModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 bg-lightPurple text-purpleDark text-sm font-semibold px-3 py-1.5 rounded-full">
              <span>🤝</span> A note from Waters
            </div>

            {/* Headline */}
            <h3 className="font-montserrat text-xl font-bold text-gray-900 uppercase">
              Why we partnered with Ownli.
            </h3>

            {/* Description */}
            <p className="text-sm text-gray-600">
              We wanted to give our members something genuinely useful — not another ad. Ownli lets you shop for insurance <strong>on your own terms</strong>, without the spam calls, and with a reward for your time.
            </p>

            {/* Benefits */}
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-lightGrey rounded-xl p-3">
                <span className="text-lg">🚫</span>
                <p className="text-sm text-gray-700">
                  <strong>No spam calls</strong> — you decide who contacts you
                </p>
              </div>
              <div className="flex items-start gap-3 bg-lightGrey rounded-xl p-3">
                <span className="text-lg">💰</span>
                <p className="text-sm text-gray-700">
                  <strong>You get paid</strong> — not Google, not Meta. You.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-lightGrey rounded-xl p-3">
                <span className="text-lg">🔒</span>
                <p className="text-sm text-gray-700">
                  <strong>Full control</strong> — nothing shared without your ok
                </p>
              </div>
            </div>

            {/* Quote */}
            <div className="bg-greenLight rounded-2xl p-4">
              <p className="text-sm text-gray-700 italic">
                "We only partner with companies that treat our members the way we do."
              </p>
              <p className="text-xs font-bold text-gray-900 mt-2">— WATERS CAR WASH</p>
            </div>

            {/* CTA */}
            <button
              onClick={() => {
                trackButtonClick("modal_sounds_good_lets_go", {
                  step_name: "welcome",
                  waters_variant: variant,
                });
                setShowOwnliModal(false);
                onComplete();
              }}
              className="w-full bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors uppercase font-bold"
            >
              Sounds good — let's go →
            </button>
          </div>
        </div>
      )}

      {/* CTA — shared */}
      <div className="space-y-3">
        <button
          onClick={() => {
            trackButtonClick("welcome_get_started", {
              is_returning_user: false,
              partner_name: "Waters",
              button_text: "See My Offers & Earn",
              waters_variant: variant,
            });
            onComplete();
          }}
          className="w-full flex uppercase font-bold items-center justify-center gap-2 bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors"
        >
          SEE MY OFFERS & EARN
          <ArrowRight className="w-4 h-4" />
        </button>

        <div className="text-center text-sm text-gray-600">
          By continuing, you agree to Ownli's{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011276138-Ownli-Privacy-Policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </div>
  );
};

export default WatersWelcome;
